import {
    ArrowRightIcon,
    FingerPrintIcon, HeroIdentificationIcon,
} from 'components/Icon'

import {
    NinchatErrorEvent, NinchatSsoBegunEvent,
} from 'api/types/events'


import React, {
    useContext, useEffect,
} from 'react'


import {
    buttonWrapper, iconWrapper, ssoContent, ssoWrapper,
    text as textStyled, textWrapper,
} from './styles'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'

import {AccessType} from 'api/types/properties'
import Button from 'components/Button'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import {Icon} from 'components/Icon/types'

import {NinchatErrorType} from 'api/types/errors'

import {beginSsoAuth} from 'api/action/beginSsoAuth'
import {config} from 'embed/listeners'
import {styled} from 'stitches/config'

import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const StyledSso = styled('div', ssoWrapper)
const StyledSsoContent = styled('div', ssoContent)
const StyledIconWrapper = styled('div', iconWrapper)
const StyledTextWrapper = styled('div', textWrapper)
const StyledHeader = styled('h2')
const StyledText = styled('div', textStyled)
const StyledButtonWrapper = styled('div', buttonWrapper)

type QuestionnaireSSOProps = {
    handleSuccess: () => void
    handleError: (error: string) => void
    accessType: AccessType
    accessUrl: string
    ssoName: string
    hideButton: boolean
    showAuthentication: boolean
    showDelegateAuthentication: boolean
}

export const QuestionnaireSSO = (props: QuestionnaireSSOProps) => {
    const {
        handleSuccess, handleError, accessType, accessUrl, ssoName,
        hideButton, showDelegateAuthentication, showAuthentication, ...other
    } = props
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const embed = useContext(EmbedContext)
    const sendToParent = useSendToParent()
    const targetOrigin = config.get('targetOrigin') || ''

    const userSso = useAppSelector((state) => state.ninchat.user_sso)
    const [status, setStatus] = React.useState<'default' | 'error' | 'inprogress' | 'success'>('default')

    const redirectTo = (ssoURL: string) => {
        sendToParent({
            event: EmbedEvent.EmbedSSO,
            targetOrigin,
            url: ssoURL,
        })
    }

    useEffect(() => {
        if (Object.keys(userSso || {}).length > 0) {
            setStatus('success')
        }
    }, [userSso])

    const handleSso = (accessHpa: boolean) => {
        if (status === 'success') {
            handleSuccess()
            return
        }

        setStatus('inprogress')
        dispatch(beginSsoAuth({
            access_hpa: accessHpa,
            access_type: accessType,
            access_url: `${accessUrl}?sso_access_key=`,
            sso_name: ssoName,
        }))
            .unwrap()
            .then(
                (event: NinchatSsoBegunEvent) => {
                    if (event.sso_url) {
                        redirectTo(event.sso_url)
                    }
                },
            )
            .catch(
                (error: NinchatErrorEvent) => {
                    setTimeout(() => {
                        setStatus('error')
                        // eslint-disable-next-line no-console
                        const {error_type} = error
                        // only redirect to next funnel view if error type is NinchatSsoNotAvailable
                        if (error_type === NinchatErrorType.NinchatSsoNotAvailable) {
                        // eslint-disable-next-line no-console
                            handleError(t('SSO not available'))
                        } else {
                        // eslint-disable-next-line no-console
                            handleError(t('SSO authentication error'))
                        }
                    }, 500)
                },
            )
    }

    const title = () => {
        if (status === 'error') {
            return t('Authentication failed')
        }
        if (status === 'success') {
            return t('Authentication successful')
        }
        if (status === 'inprogress') {
            return t('Authentication in progress')
        }
        return t('Please authenticate')
    }

    const buttonText = () => {
        if (status === 'error') {
            return t('Retry authentication')
        }
        if (status === 'success') {
            return t('Continue')
        }
        return t('Authenticate')
    }

    const text = () => {
        if (status === 'error') {
            return t('Authentication didn’t finish successfully. Please try again.')
        }
        if (status === 'success') {
            return t('Your identity is verified.')
        }
        if (status === 'inprogress') {
            return t('Your authentication is being processed. Please wait.')
        }
        return t('Before we continue, you have to verify your identity.')
    }

    return (
        <StyledSso {...other}>
            <StyledSsoContent
                lightBot={embed?.lightBot}
            >
                <StyledIconWrapper lightBot={embed?.lightBot} status={status}>
                    <FingerPrintIcon />
                </StyledIconWrapper>
                <StyledTextWrapper lightBot={embed?.lightBot}>
                    <StyledHeader>{title()}</StyledHeader>
                    <StyledText lightBot={embed?.lightBot}>{text()}</StyledText>
                </StyledTextWrapper>
            </StyledSsoContent>
            {/* eslint-disable-next-line no-negated-condition, no-extra-parens */}
            {(showAuthentication && status === 'default') || ((status === 'error' || status === 'success') && !hideButton) ?
                <StyledButtonWrapper status={status} {...status === 'success' && embed?.lightBot ? {align: 'right'} as const : {}}>
                    <Button
                        color='filled'
                        iconLeft={HeroIdentificationIcon as unknown as Icon}
                        iconRight={ArrowRightIcon}
                        onPress={() => handleSso(false)}
                        text={buttonText()}
                    />
                </StyledButtonWrapper>
                :
                null }
            {showDelegateAuthentication && status === 'default' ?
                <StyledButtonWrapper status={status}>
                    <Button
                        color='filled'
                        iconLeft={HeroIdentificationIcon as unknown as Icon}
                        iconRight={ArrowRightIcon}
                        onPress={() => handleSso(true)}
                        text={t('Authenticate as delegate')}
                    />
                </StyledButtonWrapper>
                :
                null }

        </StyledSso>
    )
}
